import { BuildingOffice2Icon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { usePractices } from '../../../hooks/useAccounts';
import type { PracticeListItem } from '../../../services/accounts';
import { OpenCallsCell } from '../practices/OpenCallsCell';
import { formatDoctorLine } from '../practices/practiceFormatting';
import { CardErrorState } from './CardErrorState';

// Accounts Overview hub's "Practices needing attention" card - top N
// practices with at least one unresolved call, ac-hub.jsx's attention list
// minus its openItems half (Action Items is cancelled, so "needing
// attention" here only ever means open calls).
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full';
const ATTENTION_LIMIT = 6;
const SKELETON_ROW_COUNT = 3;

function PracticeRow({ practice, onOpen }: { practice: PracticeListItem; onOpen: () => void }) {
    const doctorLine = formatDoctorLine(practice.doctorName);
    const location = practice.city && practice.state ? `${practice.city}, ${practice.state}` : null;
    const secondary = [doctorLine, location].filter(Boolean).join(' · ');

    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-full px-5 h-14 flex items-center gap-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-left"
        >
            <span className="w-7 h-7 rounded-lg bg-gray-100 border border-gray-200 text-gray-400 flex items-center justify-center shrink-0">
                <BuildingOffice2Icon className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-gray-900 truncate">
                    {practice.name}
                </span>
                {secondary && (
                    <span className="block text-[11px] text-gray-500 truncate">{secondary}</span>
                )}
            </span>
            <OpenCallsCell practice={practice} />
            <ChevronRightIcon className="h-4 w-4 text-gray-300 shrink-0" />
        </button>
    );
}

function SkeletonRow() {
    return (
        <div className="flex items-center gap-3.5 px-5 h-14">
            <div className="w-7 h-7 rounded-lg bg-gray-100 animate-pulse shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
                <div className="h-3 w-1/3 rounded bg-gray-100 animate-pulse" />
                <div className="h-2.5 w-1/2 rounded bg-gray-100 animate-pulse" />
            </div>
        </div>
    );
}

interface PracticesNeedingAttentionCardProps {
    className?: string;
}

export function PracticesNeedingAttentionCard({
    className = ''
}: PracticesNeedingAttentionCardProps) {
    const navigate = useNavigate();
    const { practices, isLoading, isError, refetch } = usePractices({
        page: 1,
        limit: ATTENTION_LIMIT,
        hasOpenCalls: true
    });

    const openPractice = (id: string) => navigate(`/accounts/practices/${id}`);

    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <h3 className="text-[15px] font-bold text-gray-800">Practices needing attention</h3>
                <button
                    type="button"
                    onClick={() => navigate('/accounts/practices')}
                    className="flex items-center gap-0.5 text-[13px] font-semibold text-gray-500 hover:text-gray-800 transition-colors"
                >
                    Open list
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
            </div>
            {isLoading ? (
                <div className="py-1 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : isError ? (
                <CardErrorState
                    message="Couldn't load practices needing attention"
                    onRetry={() => void refetch()}
                />
            ) : practices.length === 0 ? (
                <div className="flex-1 min-h-0 flex items-center justify-center px-6 py-8 text-center">
                    <p className="text-sm text-gray-500">No practices need attention.</p>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {practices.map((practice) => (
                        <PracticeRow
                            key={practice.id}
                            practice={practice}
                            onOpen={() => openPractice(practice.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
